// src/pages/admin/ViewFaq.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  LinearProgress,
  Pagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  getAllFaqsAdmin,
  deleteFaq,
  updateFaq,
  getAllFaqCategoriesAdmin,
} from "../../services/api";

/**
 * ViewFaq.jsx
 *
 * - Lists FAQs in a table with columns: ID, Question, Category, Published, Order, Actions
 * - Actions: Edit (open dialog), Toggle Published (Switch), Delete
 * - Supports search by question/answer and pagination
 *
 * Make sure api functions exist in src/services/api.js:
 * - getAllFaqsAdmin(params)
 * - deleteFaq(id)
 * - updateFaq(id, data)
 * - getAllFaqCategoriesAdmin(params)
 */

export default function ViewFaq() {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ total: 0, limit: 10, offset: 0 });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [snack, setSnack] = useState({ open: false, severity: "info", message: "" });

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // faq object being edited
  const [saving, setSaving] = useState(false);

  // loadCategories - stable identity with useCallback
  const loadCategories = useCallback(async () => {
    try {
      const res = await getAllFaqCategoriesAdmin({ limit: 100, includeInactive: true });
      const data = res?.data?.data || [];
      setCategories(data);
    } catch (err) {
      console.error("Failed to load categories", err);
      setSnack((s) => ({ ...s, open: true, severity: "error", message: "Failed to load categories" }));
    }
  }, []);

  // loadFaqs - stable identity; depends on meta.limit, page, search if used internally
  const loadFaqs = useCallback(
    async (opts = {}) => {
      setLoading(true);
      try {
        const limit = opts.limit ?? meta.limit ?? 10;
        const offset = opts.offset ?? (page - 1) * limit;
        const res = await getAllFaqsAdmin({
          limit,
          offset,
          search: search || undefined,
          sortBy: "order",
          orderDir: "asc",
        });
        setFaqs(res?.data?.data || []);
        const m = res?.data?.meta || {};
        setMeta({ limit: m.limit ?? limit, offset: m.offset ?? offset, total: m.total ?? 0 });
      } catch (err) {
        console.error("Failed to load faqs", err);
        setSnack((s) => ({ ...s, open: true, severity: "error", message: "Failed to load FAQs" }));
      } finally {
        setLoading(false);
      }
    },
    [meta.limit, page, search]
  );

  // initial load of categories
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // load faqs when page or search changes (loadFaqs has a stable identity via useCallback)
  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

 const handleDelete = async (id) => {
  // dynamically import SweetAlert2 (works 100% even if hot reload is on)
  const Swal = (await import("sweetalert2")).default;

  const result = await Swal.fire({
    title: "Are you sure?",
    text: "This FAQ will be permanently deleted.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it!",
    cancelButtonText: "Cancel",
    reverseButtons: true,
  });

  if (!result.isConfirmed) return;

  // Optional: show loading modal while deleting
  Swal.fire({
    title: "Deleting...",
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  setLoading(true);
  try {
    await deleteFaq(id);

    Swal.close(); // close loading state
    await Swal.fire({
      title: "Deleted!",
      text: "FAQ has been deleted successfully.",
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
    });

    setSnack({ open: true, severity: "success", message: "FAQ deleted" });
    loadFaqs();
  } catch (err) {
    console.error("deleteFaq err", err);
    Swal.close();
    Swal.fire({
      title: "Error!",
      text: "Failed to delete FAQ.",
      icon: "error",
      confirmButtonColor: "#3085d6",
    });
    setSnack({ open: true, severity: "error", message: "Failed to delete FAQ" });
  } finally {
    setLoading(false);
  }
};


  const togglePublished = async (faq) => {
    const id = faq.id;
    const newVal = !faq.isPublished;
    // optimistically update UI
    setFaqs((s) => s.map((x) => (x.id === id ? { ...x, isPublished: newVal } : x)));
    try {
      await updateFaq(id, { isPublished: newVal });
      setSnack({ open: true, severity: "success", message: `FAQ ${newVal ? "published" : "unpublished"}` });
    } catch (err) {
      console.error("togglePublished err", err);
      setSnack({ open: true, severity: "error", message: "Failed to update status" });
      // revert on error
      setFaqs((s) => s.map((x) => (x.id === id ? { ...x, isPublished: faq.isPublished } : x)));
    }
  };

  const openEdit = (faq) => {
    setEditing({
      id: faq.id,
      question: faq.question ?? "",
      answer: faq.answer ?? "",
      categoryId: faq.categoryId ?? faq.category?.id ?? "",
      order: faq.order ?? "",
      isPublished: Boolean(faq.isPublished),
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditing(null);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (!editing.question.trim()) {
      setSnack({ open: true, severity: "error", message: "Question cannot be empty" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        question: editing.question.trim(),
        answer: editing.answer?.trim() || null,
        categoryId: Number(editing.categoryId),
        isPublished: Boolean(editing.isPublished),
        order: editing.order === "" || editing.order === null ? null : Number(editing.order),
      };
      await updateFaq(editing.id, payload);
      setSnack({ open: true, severity: "success", message: "FAQ updated" });
      closeEdit();
      loadFaqs();
    } catch (err) {
      console.error("updateFaq err", err);
      setSnack({ open: true, severity: "error", message: "Failed to update FAQ" });
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || 10)));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">All FAQs</Typography>

          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search question or answer"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={() => loadFaqs()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={80}>ID</TableCell>
                <TableCell>Question</TableCell>
                <TableCell width={180}>Category</TableCell>
                <TableCell width={120}>Published</TableCell>
                <TableCell width={80}>Order</TableCell>
                <TableCell align="right" width={160}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {faqs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No FAQs found
                  </TableCell>
                </TableRow>
              ) : (
                faqs.map((faq) => (
                  <TableRow key={faq.id}>
                    <TableCell>{faq.id}</TableCell>
                    <TableCell>
                      <Typography noWrap sx={{ maxWidth: 700 }}>
                        {faq.question}
                      </Typography>
                    </TableCell>
                    <TableCell>{faq.category?.name ?? "—"}</TableCell>
                    <TableCell>
                      <Switch
                        checked={Boolean(faq.isPublished)}
                        onChange={() => togglePublished(faq)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{faq.order ?? "—"}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(faq)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(faq.id)} sx={{ ml: 1 }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {faqs.length} of {meta.total ?? 0}
          </Typography>

          <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" size="small" />
        </Box>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="md">
        <DialogTitle>Edit FAQ</DialogTitle>
        <DialogContent>
          {editing ? (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                label="Question"
                value={editing.question}
                onChange={(e) => setEditing((s) => ({ ...s, question: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="Answer"
                value={editing.answer}
                onChange={(e) => setEditing((s) => ({ ...s, answer: e.target.value }))}
                fullWidth
                multiline
                rows={6}
              />
              <FormControl fullWidth>
                <InputLabel id="edit-category-label">Category</InputLabel>
                <Select
                  labelId="edit-category-label"
                  value={editing.categoryId ?? ""}
                  label="Category"
                  onChange={(e) => setEditing((s) => ({ ...s, categoryId: e.target.value }))}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box display="flex" gap={2}>
                <TextField
                  label="Order"
                  value={editing.order ?? ""}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, order: e.target.value.replace(/[^\d]/g, "") }))
                  }
                  size="small"
                />
                <FormControl>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      Published
                    </Typography>
                    <Switch
                      checked={Boolean(editing.isPublished)}
                      onChange={(e) => setEditing((s) => ({ ...s, isPublished: e.target.checked }))}
                    />
                  </Box>
                </FormControl>
              </Box>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
